import { Link } from "@tanstack/react-router";
import type { LayerData, LayerBucket } from "@/lib/map/layers";
import { layerDescription, layerLabel } from "@/lib/map/search-params";
import { TonePattern } from "./tone-pattern";

/**
 * A swatch that shows the bucket exactly as the map paints it, colour and texture, so the
 * legend is a key rather than an approximation. Ids are namespaced away from the map's
 * own patterns because both live in the same document.
 */
function Swatch({ bucket, idPrefix = "legend" }: { bucket: LayerBucket; idPrefix?: string }) {
  // Unique per host: the key and the legend can both be in the document, one of them
  // display:none, and a url(#id) that resolves into a hidden subtree paints nothing.
  const id = `${idPrefix}-${bucket.key}`;
  return (
    <svg
      aria-hidden
      width="18"
      height="18"
      viewBox="0 0 18 18"
      className="mt-0.5 shrink-0 rounded-[3px] ring-1 ring-border"
    >
      <defs>
        <TonePattern
          id={id}
          tone={bucket.tone}
          fill={bucket.fill}
          ink={bucket.foreground}
          size={6}
          line={0.9}
        />
      </defs>
      <rect width="18" height="18" fill={`url(#${id})`} />
    </svg>
  );
}

export function MapLegend({ layer, idPrefix }: { layer: LayerData; idPrefix?: string }) {
  return (
    <section aria-label={`${layerLabel[layer.id]} legend`} className="text-sm">
      <h2 className="font-display text-lg font-medium text-foreground">{layerLabel[layer.id]}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{layerDescription[layer.id]}</p>
      <ul className="mt-3 space-y-2">
        {layer.buckets.map((b) => (
          <li key={b.key} className="flex gap-2.5">
            <Swatch bucket={b} idPrefix={idPrefix} />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground">{b.label}</p>
              <p className="text-xs leading-snug text-muted-foreground">{b.meaning}</p>
            </div>
          </li>
        ))}
      </ul>
      {layer.caveat && (
        <p className="mt-3 border-l-[3px] border-amber py-0.5 pl-3 text-xs leading-snug text-foreground">
          {layer.caveat}
        </p>
      )}
      <p className="mt-3 text-xs leading-snug text-muted-foreground">
        Our summary of published tick data. Not official surveillance, and not your personal medical
        risk.{" "}
        <Link to="/about" hash="how-we-rate-places" className="link">
          How we rate places
        </Link>
      </p>
    </section>
  );
}

/**
 * One line of swatches directly under the map, for screens without the sidebar. The
 * full legend (with a sentence per colour) used to be the only key on a phone, folded
 * shut below a 51-row list — so the first thing a reader saw was colours with no names.
 */
export function MapKey({ layer }: { layer: LayerData }) {
  return (
    <ul aria-label={`${layerLabel[layer.id]} key`} className="flex flex-wrap gap-x-4 gap-y-2">
      {layer.buckets.map((b) => (
        <li key={b.key} className="flex items-center gap-1.5 text-xs text-foreground">
          <Swatch bucket={b} idPrefix="key" />
          {b.label}
        </li>
      ))}
    </ul>
  );
}
